import { useCallback } from 'react';
import { useAnalyticsClient } from './context';
import type { EventName, EventPayload } from './types';

/**
 * `event_category` value carried by every event in the signup funnel doc.
 * Lives as a super-property, NOT a per-event payload prop — adding it to the
 * `EventMap` payloads would inflate each event's typed (and eval-scored)
 * property set. The schema/contract stays clean; the value still lands on the
 * wire via the client's super-prop merge.
 */
export const SIGN_UP_EVENT_CATEGORY = 'sign_up';

/**
 * Track wrapper for the signup funnel. Identical surface to {@link useTrack},
 * but re-stamps `event_category: 'sign_up'` as a super-property immediately
 * before each `track` call.
 *
 * Why re-stamp every time instead of registering once on funnel mount:
 * `register()` merges super-props and has no clean unset, so a session-wide
 * `event_category` set for one funnel would silently bleed into the next.
 * Stamping per-track keeps the value correct even if another funnel overwrote
 * the super-prop earlier in the session. The e2e spy asserts it lands
 * (`event.properties.event_category === 'sign_up'`).
 */
export function useSignupTrack(): <E extends EventName>(name: E, properties: EventPayload<E>) => void {
  const client = useAnalyticsClient();
  return useCallback(
    <E extends EventName>(name: E, properties: EventPayload<E>) => {
      client.register({ event_category: SIGN_UP_EVENT_CATEGORY });
      client.track(name, properties);
    },
    [client],
  );
}
